import { useMemo, type CSSProperties } from "react";
import type { FieldDefinition } from "../../../lib/types";
import { suggestField, looksLikePii } from "./mapping-utils";

/**
 * Sprint 16c — field-map editor. Left column: wrapped response paths (typed, or
 * picked from a pasted sample via the datalist). Right column: the target entity's
 * fields. Each row connects one response path → one entity field. Name-similarity
 * auto-suggest prefills the right side; already-mapped fields are disabled so each
 * entity field is a target at most once. (A pragmatic two-column mapper — the
 * "drag-drop" the spec describes, without the react-dnd weight its risk note warns off.)
 */
export function FieldMapVisual({
  fields,
  fieldMap,
  onChange,
  discoveredPaths = [],
}: {
  fields: FieldDefinition[];
  fieldMap: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  discoveredPaths?: string[];
}) {
  // Ordered pairs so the owner can edit the wrapped path (the record key).
  const pairs = useMemo(() => Object.entries(fieldMap), [fieldMap]);
  const usedFields = new Set(pairs.map(([, f]) => f).filter(Boolean));

  function commit(next: Array<[string, string]>) {
    const rec: Record<string, string> = {};
    for (const [k, v] of next) {
      if (k.trim() && v.trim()) rec[k.trim()] = v.trim();
    }
    onChange(rec);
  }

  function setRow(i: number, key: string, value: string) {
    const next = pairs.map((p) => [...p] as [string, string]);
    next[i] = [key, value];
    // Auto-suggest an entity field when the path changed and the target is empty.
    if (value === pairs[i]?.[1] && !value) {
      const s = suggestField(key, fields.filter((f) => !usedFields.has(f.name)));
      if (s) next[i] = [key, s];
    }
    commit(next);
  }

  function addRow() {
    commit([...pairs.map((p) => [...p] as [string, string]), ["", ""]]);
  }

  function removeRow(i: number) {
    commit(pairs.filter((_, idx) => idx !== i).map((p) => [...p] as [string, string]));
  }

  const listId = "wrapped-paths-datalist";

  return (
    <div>
      {discoveredPaths.length > 0 && (
        <datalist id={listId}>
          {discoveredPaths.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
      )}
      <div style={headerRow}>
        <span style={colHead}>Response field</span>
        <span style={{ width: 18 }} />
        <span style={colHead}>Entity field</span>
        <span style={{ width: 26 }} />
      </div>
      {pairs.length === 0 && (
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", padding: "6px 0" }}>
          No fields mapped yet. Add at least one to materialise rows.
        </div>
      )}
      {pairs.map(([wrappedPath, entityField], i) => (
        <div key={i} style={mapRow}>
          <input
            value={wrappedPath}
            list={listId}
            placeholder="e.g. requester.email"
            onChange={(e) => setRow(i, e.target.value, entityField)}
            style={{ ...input, fontFamily: "var(--font-mono)" }}
          />
          <span style={connector} aria-hidden="true">→</span>
          <select
            value={entityField}
            onChange={(e) => setRow(i, wrappedPath, e.target.value)}
            style={input}
          >
            <option value="">— field —</option>
            {fields.map((f) => (
              <option key={f.name} value={f.name} disabled={usedFields.has(f.name) && f.name !== entityField}>
                {f.name} ({f.type}){f.required ? " *" : ""}{looksLikePii(f.name) ? " ⚠" : ""}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => removeRow(i)} style={removeBtn} aria-label="Remove mapping">
            <i className="ti ti-x" style={{ fontSize: 13 }} aria-hidden="true" />
          </button>
        </div>
      ))}
      <button type="button" onClick={addRow} style={addBtn}>
        <i className="ti ti-plus" style={{ fontSize: 12 }} aria-hidden="true" /> Add field mapping
      </button>
      {pairs.some(([, f]) => looksLikePii(f)) && (
        <div style={piiWarn}>
          <i className="ti ti-alert-triangle" style={{ fontSize: 12 }} aria-hidden="true" /> ⚠ marks fields that look
          like PII (email/phone/etc.). Confirm the target entity type's access is appropriate before saving.
        </div>
      )}
    </div>
  );
}

const headerRow: CSSProperties = { display: "flex", alignItems: "center", gap: 0, marginBottom: 4 };
const colHead: CSSProperties = { flex: 1, fontSize: 11, fontWeight: 500, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em" };
const mapRow: CSSProperties = { display: "flex", alignItems: "center", gap: 0, marginBottom: 6 };
const input: CSSProperties = {
  flex: 1,
  minWidth: 0,
  boxSizing: "border-box",
  padding: "6px 8px",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
};
const connector: CSSProperties = { width: 18, textAlign: "center", color: "var(--color-text-tertiary)", fontSize: 13, flexShrink: 0 };
const removeBtn: CSSProperties = {
  width: 26,
  flexShrink: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
const addBtn: CSSProperties = {
  fontSize: 12,
  padding: "5px 10px",
  marginTop: 4,
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const piiWarn: CSSProperties = {
  fontSize: 11,
  color: "var(--c-amber-800, var(--color-text-secondary))",
  marginTop: 8,
  lineHeight: 1.5,
  display: "flex",
  gap: 4,
  alignItems: "baseline",
};
